import type {
  AcceptedWikiSuggestion,
  ConversationDetail,
  DismissalReason,
  WikiSuggestionView,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookCheck, BookPlus, ChevronRight, FileText, Globe, X } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { DismissReasons } from "@/components/DismissReasons";
import { Markdown } from "@/components/markdown/Markdown";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { csrf, csrfJson, done } from "../account/queries.ts";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { saveRefusal } from "../conversation/SaveDialog.tsx";
import { changeSetPath, Link } from "../router.tsx";
import { useSession } from "../session.tsx";
import { wikiKeys } from "./queries.ts";

/**
 * How the caller decided a suggestion, kept on the chat's part (client-only) so a chat kept
 * across route changes shows it settled rather than offering the buttons again.
 */
export type WikiSuggestionDecision =
  | { state: "dismissed" }
  | { state: "drafted"; projectId: string; changeSetId: string };

const suggestionRoute =
  api.api.conversations[":conversationId"]["wiki-suggestions"][":suggestionId"];

/**
 * "Draft it" and "Dismiss" on the caller's suggestion. A decided suggestion leaves the cached
 * conversation, so reopening it never brings the card back.
 */
function useSuggestionDecision(
  conversationId: string,
  suggestion: WikiSuggestionView,
  settle: (decision: WikiSuggestionDecision) => void,
) {
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const param = { conversationId, suggestionId: suggestion.id };
  const forget = () =>
    queryClient.setQueriesData<ConversationDetail>({ queryKey: ["conversation"] }, (detail) =>
      detail?.wikiSuggestions
        ? {
            ...detail,
            wikiSuggestions: detail.wikiSuggestions.filter((s) => s.id !== suggestion.id),
          }
        : detail,
    );
  const route = suggestionRoute;
  return {
    accept: useMutation({
      mutationFn: async (): Promise<AcceptedWikiSuggestion> =>
        ok(await route.accept.$post({ param }, csrf(csrfToken))),
      onSuccess: (accepted) => {
        // Settled on the chat first: the refreshed conversation no longer has it.
        settle({
          state: "drafted",
          projectId: accepted.projectId,
          changeSetId: accepted.changeSetId,
        });
        forget();
        return queryClient.invalidateQueries({ queryKey: wikiKeys.all(accepted.projectId) });
      },
    }),
    /** The optional reason, after Dismiss (recommendation-outcomes). */
    reason: async (reason: DismissalReason) =>
      done(await route.dismiss.$post({ param }, csrfJson(csrfToken, { reason }))),
    dismiss: useMutation({
      mutationFn: async () => done(await route.dismiss.$post({ param }, csrf(csrfToken))),
      onSuccess: () => {
        settle({ state: "dismissed" });
        forget();
      },
    }),
  };
}

/**
 * A wiki page the assistant suggests after an answer (openspec change `wiki-suggestions`): what
 * would be drafted and why, with Draft it and Dismiss. Nothing enters the review queue without
 * the user's click, and nothing is published without a reviewer. Dashed while undecided, like
 * the memory card; an accepted suggestion settles into a quiet line linking to its review.
 */
export function WikiSuggestionCard(props: {
  conversationId: string;
  suggestion: WikiSuggestionView;
  /** A decision taken earlier on this chat (the card was unmounted since). */
  decided?: WikiSuggestionDecision | undefined;
  onDecided?: (decision: WikiSuggestionDecision) => void;
  /** False once the next turn started: a dismissal no longer asks why. */
  askWhy?: boolean | undefined;
}) {
  const { conversationId, suggestion } = props;
  const { t } = useTranslation();
  const id = useId();
  const [decision, setDecision] = useState<WikiSuggestionDecision | null>(props.decided ?? null);
  // Decided before this card mounted (the chat keeps the decision): no reasons are offered.
  const [decidedEarlier] = useState(props.decided !== undefined);
  const { accept, dismiss, reason } = useSuggestionDecision(conversationId, suggestion, (next) => {
    setDecision(next);
    props.onDecided?.(next);
  });
  const error = accept.error ?? dismiss.error;
  const gone = error instanceof ApiError && error.status === 404;
  const busy = accept.isPending || dismiss.isPending;
  const blocked = suggestion.blocked;
  const [first] = suggestion.pages;
  const single = suggestion.pages.length === 1 ? first : undefined;

  if (decision?.state === "dismissed") {
    // Dismissed here and now: why, optionally; an earlier decision only says it.
    return (
      <DismissReasons onReason={reason} offer={!decidedEarlier && props.askWhy !== false}>
        {t("wikiSuggestion.dismissed")}
      </DismissReasons>
    );
  }
  if (decision?.state === "drafted") {
    return (
      <p
        role="status"
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted"
      >
        <BookCheck aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        <span>{t("wikiSuggestion.drafted")}</span>
        <Link
          href={changeSetPath(decision.projectId, decision.changeSetId)}
          className="text-accent-text underline-offset-4 hover:underline"
        >
          {t("wiki.reviewDraft")}
        </Link>
      </p>
    );
  }

  return (
    <section
      data-esi-content="wiki"
      aria-labelledby={`${id}-title`}
      className={cn(
        "flex flex-col gap-2.5 rounded-lg border border-dashed border-line-strong px-3.5 py-3",
        "transition-colors duration-(--duration-fast) ease-(--ease-out) focus-within:border-solid",
      )}
    >
      <div className="flex items-start gap-2.5">
        <BookPlus
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 id={`${id}-title`} className="text-[13px] font-medium text-fg-secondary">
            {suggestion.source === "tool"
              ? t("wikiSuggestion.proposed", { count: suggestion.pages.length })
              : t("wikiSuggestion.suggested")}
          </h3>
          {single ? (
            <p className="text-sm font-medium text-fg">
              {single.kind === "update"
                ? t("wikiSuggestion.changeTo", { page: single.pageTitle ?? single.title })
                : single.title}
            </p>
          ) : null}
          {suggestion.reason ? (
            <p className="text-sm leading-relaxed text-fg-secondary">{suggestion.reason}</p>
          ) : null}
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted">
            <span>{t("wikiSuggestion.reviewNote")}</span>
            {suggestion.sourceCount > 0 ? (
              <>
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1">
                  <FileText aria-hidden="true" className="size-3" strokeWidth={1.75} />
                  {t("wikiSuggestion.sources", { count: suggestion.sourceCount })}
                </span>
              </>
            ) : null}
          </p>
          {suggestion.trust === "external" ? (
            <p className="flex items-center gap-1 text-xs text-fg-muted">
              <Globe aria-hidden="true" className="size-3 shrink-0" strokeWidth={1.75} />
              {t("wikiSuggestion.external")}
            </p>
          ) : null}
        </div>
      </div>
      {suggestion.pages.some((p) => p.written) || !single ? (
        <ul className="ml-6.5 divide-y divide-line rounded-lg border border-line bg-surface-raised">
          {/* Titles are unique within a suggestion (a repeated one replaces the page). */}
          {suggestion.pages.map((page, index) => (
            <li key={page.title} className="px-3 py-2">
              <ProposedPage
                page={page}
                showTitle={!single}
                source={{ conversationId, suggestionId: suggestion.id, index }}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {error ? (
        <InlineError>
          {gone
            ? t("wikiSuggestion.gone")
            : error instanceof ApiError && error.code === "wiki_suggestions_disabled"
              ? t("wikiSuggestion.disabled")
              : error instanceof ApiError && error.code === "change_set_changed"
                ? t("wikiSuggestion.changed")
                : error instanceof ApiError
                  ? saveRefusal(error, t)
                  : t("errors.network")}
        </InlineError>
      ) : null}
      {gone ? null : (
        <div className="flex flex-wrap items-center gap-1.5 pl-6.5">
          <Button
            type="button"
            size="sm"
            disabled={busy || blocked !== undefined}
            {...(blocked && { "aria-describedby": `${id}-blocked` })}
            onClick={() => accept.mutate()}
          >
            {accept.isPending ? t("wikiSuggestion.drafting") : t("wikiSuggestion.draft")}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            className="text-fg-secondary"
            onClick={() => dismiss.mutate()}
          >
            <X aria-hidden="true" strokeWidth={1.75} />
            {t("wikiSuggestion.dismiss")}
          </Button>
          {blocked ? (
            <p id={`${id}-blocked`} className="text-xs text-fg-muted">
              {t(`wikiSuggestion.${blocked}`)}
            </p>
          ) : null}
        </div>
      )}
    </section>
  );
}

/**
 * One page of a suggestion: its title and kind, and the agent's page behind a disclosure, read
 * when it first opens (never sent with the conversation).
 */
function ProposedPage(props: {
  page: WikiSuggestionView["pages"][number];
  showTitle: boolean;
  source: { conversationId: string; suggestionId: string; index: number };
}) {
  const { page, showTitle, source } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const panel = useId();
  const content = useQuery({
    queryKey: ["wiki-suggestion-page", source.suggestionId, source.index],
    queryFn: async () =>
      ok(
        await suggestionRoute.pages[":index"].$get({
          param: {
            conversationId: source.conversationId,
            suggestionId: source.suggestionId,
            index: String(source.index),
          },
        }),
      ),
    enabled: open && page.written === true,
    // The page never changes while the suggestion is pending.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  return (
    <div className="flex flex-col gap-1.5">
      {showTitle ? (
        <p className="text-sm text-fg">
          {page.kind === "update"
            ? t("wikiSuggestion.changeTo", { page: page.pageTitle ?? page.title })
            : page.title}
        </p>
      ) : null}
      {page.written ? (
        <>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={panel}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex w-fit items-center gap-1 text-xs font-medium text-fg-secondary hover:text-fg"
          >
            <ChevronRight
              aria-hidden="true"
              className={cn(
                "size-3.5 transition-transform duration-(--duration-fast) ease-(--ease-out)",
                open && "rotate-90",
              )}
              strokeWidth={1.75}
            />
            {open ? t("wikiSuggestion.hidePage") : t("wikiSuggestion.showPage")}
            {showTitle ? <span className="sr-only">: {page.title}</span> : null}
          </button>
          <div id={panel} hidden={!open} className="max-h-80 overflow-y-auto pt-1">
            {!open ? null : content.data ? (
              <Markdown markdown={content.data.markdown} />
            ) : content.error ? (
              <InlineError>
                {content.error instanceof ApiError && content.error.status === 404
                  ? t("wikiSuggestion.gone")
                  : t("errors.network")}
              </InlineError>
            ) : (
              <p role="status" className="text-xs text-fg-muted">
                {t("wikiSuggestion.loadingPage")}
              </p>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
